
(function () {
  const PROPERTY_KEY = "_fundraiser_campaign_code";
  const ATTRIBUTE_KEY = "fundraiser_delivery_method";
  const configScript = document.querySelector(
    "script[data-fundraiser-delivery-script]"
  );
  const SCHOOL_PICKUP_DISCOUNT =
    (configScript && configScript.dataset.schoolPickupDiscountCode
      ? configScript.dataset.schoolPickupDiscountCode
      : ""
    ).trim();
  const CART_LINES_UPDATE = "shopify:cart:lines-update";
  const CART_DISCOUNT_UPDATE = "shopify:cart:discount-update";
  const VALID_VALUES = ["school_pickup", "home_delivery"];

  /** Serialize attribute + discount updates so rapid changes don't race */
  let updateQueue = Promise.resolve();
  /** Skip discount re-sync while we are already applying/removing */
  let discountSyncLock = false;

  function getRoots() {
    return Array.from(document.querySelectorAll("[data-fundraiser-delivery]"));
  }

  function cartUpdateUrl() {
    return (
      (window.Theme && Theme.routes && Theme.routes.cart_update_url) ||
      "/cart/update.js"
    );
  }

  function isFundraiserItem(item) {
    return Boolean(item && item.properties && item.properties[PROPERTY_KEY]);
  }

  function cartHasFundraiser(cart) {
    return Boolean(
      cart && Array.isArray(cart.items) && cart.items.some(isFundraiserItem)
    );
  }

  function getSavedMethod(cart) {
    const value =
      cart && cart.attributes ? cart.attributes[ATTRIBUTE_KEY] : "";
    return VALID_VALUES.includes(value) ? value : "";
  }

  function getDiscountCodes(cart) {
    if (!cart || !Array.isArray(cart.discount_codes)) return [];
    return cart.discount_codes
      .map((entry) => (entry && entry.code ? String(entry.code) : ""))
      .filter(Boolean);
  }

  function findDiscountEntry(cart, code) {
    if (!code) return null;
    if (!cart || !Array.isArray(cart.discount_codes)) return null;
    const needle = code.toLowerCase();
    return (
      cart.discount_codes.find(
        (entry) => entry && entry.code && entry.code.toLowerCase() === needle
      ) || null
    );
  }

  function hasSchoolPickupDiscount(cart) {
    return Boolean(findDiscountEntry(cart, SCHOOL_PICKUP_DISCOUNT));
  }

  function isSchoolPickupDiscountApplicable(cart) {
    const entry = findDiscountEntry(cart, SCHOOL_PICKUP_DISCOUNT);
    return Boolean(entry && entry.applicable);
  }

  /**
   * Build the full discount string for /cart/update.js.
   * Always preserves unrelated codes; only adds/removes the configured code.
   */
  function buildDiscountParam(cart, includeSchoolPickup) {
    const preserved = getDiscountCodes(cart).filter(
      (code) => code.toLowerCase() !== SCHOOL_PICKUP_DISCOUNT.toLowerCase()
    );

    if (includeSchoolPickup) {
      preserved.push(SCHOOL_PICKUP_DISCOUNT);
    }

    return preserved.join(",");
  }

  function getCartSectionIds() {
    const ids = new Set();
    document
      .querySelectorAll(
        "cart-items-component[data-section-id], cart-discount-component[data-section-id]"
      )
      .forEach((el) => {
        if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
      });
    return Array.from(ids);
  }

  async function fetchCart() {
    const res = await fetch("/cart.js", {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) throw new Error("Failed to fetch cart");
    return res.json();
  }

  /**
   * Tell Horizon cart components to morph/re-render after a discount change.
   * Uses the same shopify:cart:discount-update channel as cart-discount.js.
   */
  function notifyDiscountUpdate(data) {
    let resolveFn = function () {};
    const promise = new Promise(function (resolve) {
      resolveFn = resolve;
    });

    const event = new Event(CART_DISCOUNT_UPDATE, { bubbles: true });
    Object.defineProperties(event, {
      promise: { value: promise },
      discountCodes: {
        value: (data.discount_codes || []).map(function (entry) {
          return { code: entry.code };
        }),
      },
    });

    document.dispatchEvent(event);

    resolveFn({
      cart: {
        id: data.token,
        totalQuantity: data.item_count,
        cost: {
          totalAmount: {
            amount: String(data.total_price != null ? data.total_price : 0),
            currencyCode: data.currency || "",
          },
        },
        lines: [],
        discountCodes: data.discount_codes || [],
      },
      detail: data.sections ? { sections: data.sections } : undefined,
    });
  }

  /**
   * Single cart/update.js call for attribute and/or discount.
   * Matches Horizon's supported discount persistence into checkout.
   */
  async function updateCart(payload) {
    const sectionIds = getCartSectionIds();
    const body = Object.assign({}, payload);
    if (sectionIds.length) {
      body.sections = sectionIds;
    }

    const res = await fetch(cartUpdateUrl(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!res.ok) throw new Error("Cart update failed");

    const data = await res.json();

    if (Object.prototype.hasOwnProperty.call(payload, "discount")) {
      notifyDiscountUpdate(data);
    }

    return data;
  }

  function enqueueUpdate(task) {
    updateQueue = updateQueue.then(task, task);
    return updateQueue;
  }

  /**
   * Desired rule:
   * fundraiser items + school_pickup → include the configured discount
   * otherwise → exclude the configured discount (leave other codes alone)
   */
  function shouldIncludeSchoolPickupDiscount(cart, deliveryMethod) {
    return (
      Boolean(SCHOOL_PICKUP_DISCOUNT) &&
      cartHasFundraiser(cart) &&
      deliveryMethod === "school_pickup"
    );
  }

  async function syncSchoolPickupDiscount(cart, deliveryMethod) {
    if (!SCHOOL_PICKUP_DISCOUNT) return cart;

    const include = shouldIncludeSchoolPickupDiscount(cart, deliveryMethod);
    const currentlyHas = hasSchoolPickupDiscount(cart);

    if (include === currentlyHas) {
      return cart;
    }

    discountSyncLock = true;
    try {
      const data = await updateCart({
        discount: buildDiscountParam(cart, include),
      });
      return data;
    } finally {
      discountSyncLock = false;
    }
  }

  function clearError(root) {
    const error = root.querySelector("[data-fundraiser-delivery-error]");
    const select = root.querySelector("[data-fundraiser-delivery-select]");
    if (error) error.hidden = true;
    if (select) select.classList.remove("is-invalid");
  }

  function showError(root) {
    const error = root.querySelector("[data-fundraiser-delivery-error]");
    const select = root.querySelector("[data-fundraiser-delivery-select]");
    if (error) {
      error.hidden = false;
    }
    if (select) {
      select.classList.add("is-invalid");
      select.focus();
    }
  }

  function updateDescriptions(root, value) {
    root.querySelectorAll("[data-fundraiser-delivery-desc]").forEach((el) => {
      el.hidden = el.getAttribute("data-fundraiser-delivery-desc") !== value;
    });
  }

  /**
   * Status messages reflect real discount_codes from /cart.js — not fake £0 copy.
   */
  function updateDiscountStatus(root, cart, deliveryMethod) {
    const applied = root.querySelector(
      '[data-fundraiser-delivery-status="school_pickup_applied"]'
    );
    const failed = root.querySelector(
      '[data-fundraiser-delivery-status="school_pickup_failed"]'
    );
    const home = root.querySelector(
      '[data-fundraiser-delivery-status="home_delivery"]'
    );

    const showApplied =
      deliveryMethod === "school_pickup" &&
      isSchoolPickupDiscountApplicable(cart);
    const showFailed =
      deliveryMethod === "school_pickup" &&
      Boolean(SCHOOL_PICKUP_DISCOUNT) &&
      !isSchoolPickupDiscountApplicable(cart);
    const showHome = deliveryMethod === "home_delivery";

    if (applied) applied.hidden = !showApplied;
    if (failed) failed.hidden = !showFailed;
    if (home) home.hidden = !showHome;
  }

  function applyState(root, { visible, value, cart }) {
    root.hidden = !visible;

    if (visible) {
      root.setAttribute(
        "data-has-selection",
        VALID_VALUES.includes(value) ? "true" : "false"
      );
    } else {
      root.setAttribute("data-has-selection", "false");
      clearError(root);
    }

    const select = root.querySelector("[data-fundraiser-delivery-select]");
    if (select) {
      // Never leave required on a hidden control — it can block normal cart checkout
      select.required = visible;
      select.setAttribute("aria-required", visible ? "true" : "false");

      if (select.value !== value) {
        select.value = value;
      }
    }

    updateDescriptions(root, value);
    updateDiscountStatus(root, cart || {}, value);
  }

  async function clearDeliveryAttributeIfNeeded(cart) {
    if (cartHasFundraiser(cart)) return;
    if (!getSavedMethod(cart)) return;

    let latest;
    try {
      latest = await fetchCart();
    } catch (err) {
      console.warn("[fundraiser-delivery] clear re-check failed", err);
      return;
    }

    if (cartHasFundraiser(latest)) return;
    if (!getSavedMethod(latest)) return;

    await updateCart({
      attributes: { [ATTRIBUTE_KEY]: "" },
    });
  }

  /**
   * Sync UI + ensure the configured discount matches delivery selection.
   */
  async function syncFromCart() {
    if (discountSyncLock) return;

    let cart;
    try {
      cart = await fetchCart();
    } catch (err) {
      console.warn("[fundraiser-delivery] cart sync failed", err);
      return;
    }

    const hasFundraiser = cartHasFundraiser(cart);
    const saved = hasFundraiser ? getSavedMethod(cart) : "";

    getRoots().forEach((root) => {
      applyState(root, {
        visible: hasFundraiser,
        value: saved,
        cart: cart,
      });
    });

    await clearDeliveryAttributeIfNeeded(cart);

    // Keep real Shopify discount in sync with selection / fundraiser presence
    try {
      const refreshed = await syncSchoolPickupDiscount(cart, saved);
      const nextCart = refreshed || cart;
      const nextHasFundraiser = cartHasFundraiser(nextCart);
      const nextMethod = nextHasFundraiser
        ? getSavedMethod(nextCart) || saved
        : "";

      getRoots().forEach((root) => {
        applyState(root, {
          visible: nextHasFundraiser,
          value: nextMethod,
          cart: nextCart,
        });
      });
    } catch (err) {
      console.warn("[fundraiser-delivery] discount sync failed", err);
    }
  }

  function anyVisibleFundraiserForm() {
    return getRoots().some((root) => !root.hidden);
  }

  function currentSelection() {
    const visibleRoot = getRoots().find((root) => !root.hidden);
    if (!visibleRoot) return { required: false, value: "", root: null };

    const select = visibleRoot.querySelector(
      "[data-fundraiser-delivery-select]"
    );
    const value = select ? select.value : "";
    return {
      required: true,
      value: value,
      root: visibleRoot,
      valid: VALID_VALUES.includes(value),
    };
  }

  function validateBeforeCheckout() {
    if (!anyVisibleFundraiserForm()) return true;

    const state = currentSelection();
    if (state.valid) {
      if (state.root) clearError(state.root);
      return true;
    }

    if (state.root) showError(state.root);
    return false;
  }

  function isCheckoutTrigger(target) {
    if (!(target instanceof Element)) return false;

    return Boolean(
      target.closest(
        '#checkout, button[name="checkout"], .cart__checkout-button, [href="/checkout"], a[href$="/checkout"]'
      )
    );
  }

  async function onSelectChange(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    if (!select.matches("[data-fundraiser-delivery-select]")) return;

    const root = select.closest("[data-fundraiser-delivery]");
    if (!root) return;

    const value = select.value;
    clearError(root);
    updateDescriptions(root, value);
    root.setAttribute(
      "data-has-selection",
      VALID_VALUES.includes(value) ? "true" : "false"
    );

    await enqueueUpdate(async function () {
      let cart;
      try {
        cart = await fetchCart();
      } catch (err) {
        console.warn("[fundraiser-delivery] cart fetch failed", err);
        return;
      }

      // Only auto-apply discount on fundraiser carts
      const method = VALID_VALUES.includes(value) ? value : "";
      const includeDiscount = shouldIncludeSchoolPickupDiscount(cart, method);

      discountSyncLock = true;
      try {
        const payload = {
          attributes: {
            [ATTRIBUTE_KEY]: method,
          },
        };

        if (SCHOOL_PICKUP_DISCOUNT) {
          payload.discount = buildDiscountParam(cart, includeDiscount);
        }

        const data = await updateCart(payload);

        applyState(root, {
          visible: true,
          value: method,
          cart: data,
        });

        // Re-check applicable flag from a fresh cart.js if sections response is thin
        if (method === "school_pickup") {
          try {
            const verified = await fetchCart();
            updateDiscountStatus(root, verified, method);
          } catch (_err) {
            /* status already set from update response */
          }
        }
      } catch (err) {
        console.warn("[fundraiser-delivery] selection update failed", err);
        updateDiscountStatus(root, cart, method);
      } finally {
        discountSyncLock = false;
      }
    });
  }

  function onCheckoutClick(event) {
    if (!isCheckoutTrigger(event.target)) return;
    if (validateBeforeCheckout()) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  function onCartFormSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.id !== "cart-form") return;
    if (validateBeforeCheckout()) return;

    event.preventDefault();
    event.stopPropagation();
  }

  function onCartLinesUpdate(event) {
    const settle = function () {
      requestAnimationFrame(function () {
        syncFromCart();
      });
    };

    if (event && event.promise && typeof event.promise.then === "function") {
      event.promise.then(settle, settle);
    } else {
      settle();
    }
  }

  function init() {
    document.addEventListener("change", onSelectChange);
    document.addEventListener("click", onCheckoutClick, true);
    document.addEventListener("submit", onCartFormSubmit, true);
    document.addEventListener(CART_LINES_UPDATE, onCartLinesUpdate);

    syncFromCart();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
